import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  GlobeAltIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import getEventDateRange from 'utils/eventDateRange';
import CategoryList from 'components/CategoryList';
import facebookIcon from '../../assets/icons/socials/facebook_icon.svg';
import instagramIcon from '../../assets/icons/socials/instagram_icon.svg';
import tiktokIcon from '../../assets/icons/socials/tiktok_icon.svg';
import twitterIcon from '../../assets/icons/socials/twitter_icon.svg';
import linkedinIcon from '../../assets/icons/socials/linkedin_icon.svg';

const cx = (...a) => a.filter(Boolean).join(' ');

const normalizeUrl = (url) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

// --- Date helpers ----------------------------------------------------------
const fmtMonthShort = (d, locale = 'en') =>
  moment(d).locale(locale).format('MMM').toUpperCase();

const fmtRange = (rawDate, locale = 'en') => {
  const range = getEventDateRange(rawDate);
  if (!range) return '';
  const s = moment(range.startDate).locale(locale);
  const e = moment(range.endDate).locale(locale);
  const sameMonth = s.month() === e.month() && s.year() === e.year();
  const monthS = s.format('MMM').toUpperCase();
  const monthE = e.format('MMM').toUpperCase();
  if (sameMonth) return `${s.date()}–${e.date()} ${monthS} ${s.year()}`;
  return `${s.date()} ${monthS} – ${e.date()} ${monthE} ${s.year()}`;
};

const daysCount = (rawDate) => {
  const range = getEventDateRange(rawDate);
  if (!range) return 0;
  return moment(range.endDate).diff(moment(range.startDate), 'days') + 1;
};

const fmtEUR = (n) => `€${Number(n || 0).toLocaleString('en-GB')}`;

// --- DTSocials -------------------------------------------------------------
// Row of round social icon buttons. Uses the existing brand SVG assets.
export function DTSocials({ socials, size = 36, className }) {
  const s = socials || {};
  const items = [];
  if (s.website) {
    items.push({
      key: 'website',
      url: normalizeUrl(socials.website),
      label: 'Website',
      svg: (
        <GlobeAltIcon
          className="text-gray-600"
          style={{ width: size * 0.44, height: size * 0.44 }}
        />
      ),
    });
  }
  if (s.instagram) {
    items.push({
      key: 'instagram',
      url: normalizeUrl(s.instagram),
      label: 'Instagram',
      svg: <img src={instagramIcon} alt="" style={{ width: size * 0.5, height: size * 0.5 }} />,
    });
  }
  if (s.facebook) {
    items.push({
      key: 'facebook',
      url: normalizeUrl(s.facebook),
      label: 'Facebook',
      svg: <img src={facebookIcon} alt="" style={{ width: size * 0.5, height: size * 0.5 }} />,
    });
  }
  if (s.twitter) {
    items.push({
      key: 'twitter',
      url: normalizeUrl(s.twitter),
      label: 'Twitter',
      svg: <img src={twitterIcon} alt="" style={{ width: size * 0.5, height: size * 0.5 }} />,
    });
  }
  if (s.tiktok) {
    items.push({
      key: 'tiktok',
      url: normalizeUrl(s.tiktok),
      label: 'TikTok',
      svg: <img src={tiktokIcon} alt="" style={{ width: size * 0.5, height: size * 0.5 }} />,
    });
  }
  if (s.linkedin) {
    items.push({
      key: 'linkedin',
      url: normalizeUrl(s.linkedin),
      label: 'LinkedIn',
      svg: <img src={linkedinIcon} alt="" style={{ width: size * 0.5, height: size * 0.5 }} />,
    });
  }

  if (!items.length) return null;
  return (
    <div className={cx('flex items-center gap-1.5', className)}>
      {items.map(({ key, url, label, svg }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className="grid place-items-center rounded-full bg-paper text-gray-600 hover:text-ink-strong hover:bg-line transition"
          style={{ width: size, height: size }}
        >
          {svg}
        </a>
      ))}
    </div>
  );
}

// --- LPDayBlock ------------------------------------------------------------
// Big day-of-month chip with month abbreviation.
export function LPDayBlock({ date, tone = 'ink', className }) {
  const range = getEventDateRange(date);
  if (!range) return null;
  const s = moment(range.startDate);
  const e = moment(range.endDate);
  const sameDay = s.isSame(e, 'day');
  const month = fmtMonthShort(s);
  const tones = {
    ink: 'bg-ink-strong text-white',
    amber: 'bg-brand text-ink-strong',
    paper: 'bg-paper text-ink-strong ring-1 ring-line',
  };
  return (
    <div
      className={cx(
        'w-14 h-16 rounded-2xl flex flex-col items-center justify-center shrink-0 leading-none',
        tones[tone],
        className
      )}
    >
      <span className="text-[9.5px] tracking-[0.12em] font-bold opacity-80 mb-0.5">
        {month}
      </span>
      <span className="text-[22px] font-extrabold tabular-nums">
        {sameDay ? s.date() : `${s.date()}–${e.date()}`}
      </span>
    </div>
  );
}

// --- SectionActionLink -----------------------------------------------------
// The one affordance every brand-page section uses to open its "see the rest"
// popup. Kept in a single place so the lettering never drifts between the
// Portfolio, About, Categories, Upcoming and Track-record headers.
export function SectionActionLink({ label, onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      className={cx(
        'shrink-0 inline-flex items-center gap-1 text-[12px] font-bold',
        'text-ink-strong hover:underline focus:outline-none',
        'focus-visible:ring-2 focus-visible:ring-brand rounded',
        className
      )}
    >
      <span>{label}</span>
      <ArrowRightIcon className="w-3 h-3" />
    </button>
  );
}

// --- SectionHeader ---------------------------------------------------------
// Eyebrow + title on the left, the action link on the right, baseline-aligned
// with the title. Every section on the page renders through this so the
// action links all sit in the same place.
export function SectionHeader({ eyebrow, title, action, titleClassName }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[10.5px] font-bold tracking-[0.18em] text-brand uppercase">
          {eyebrow}
        </p>
        <h2
          className={cx(
            'mt-1 font-extrabold tracking-tight text-ink-strong leading-tight',
            titleClassName || 'text-[20px]'
          )}
        >
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

// --- Sidebar card shell ----------------------------------------------------
export function SidebarCard({ eyebrow, children, className }) {
  return (
    <div
      className={cx(
        'rounded-3xl bg-white ring-1 ring-line shadow-card p-5',
        className
      )}
    >
      {eyebrow && (
        <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand">
          {eyebrow}
        </div>
      )}
      {children}
    </div>
  );
}

// --- BrandUpcomingEventRow -------------------------------------------------
// Full-width featured row used in the "Upcoming events" section.
export function BrandUpcomingEventRow({ event, isAuthenticated }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  if (!event) return null;
  const range = fmtRange(event.date, i18n.language);
  const days = daysCount(event.date);
  const thumb = event.avatar || event.landscapeImage;
  const locationLine = [event.city, event.country].filter(Boolean).join(', ');
  const dateLine = [range, days ? t('brand_detail.days_short', { count: days }) : null]
    .filter(Boolean)
    .join(' · ');

  const go = () => {
    if (event.slug) navigate(`/event/${event.slug}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="flex items-center gap-4 p-3 pr-5 bg-white rounded-3xl ring-1 ring-line shadow-card hover:shadow-md transition cursor-pointer"
    >
      <div className="relative w-[100px] h-[100px] rounded-2xl overflow-hidden ring-1 ring-line shrink-0 bg-paper">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-paper-2 text-gray-300">
            <BuildingStorefrontIcon className="w-8 h-8" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-extrabold text-[16px] text-ink-strong tracking-tight truncate">
          {event.name}
        </h3>
        {dateLine && (
          <p className="text-[12px] text-gray-500 mt-0.5 truncate">{dateLine}</p>
        )}
        {locationLine && (
          <p className="mt-0.5 flex items-center gap-1 text-[11.5px] text-gray-400 truncate">
            <MapPinIcon className="w-3 h-3 shrink-0" />
            <span className="truncate">{locationLine}</span>
          </p>
        )}
      </div>
      <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
        {Array.isArray(event.categoriesAllowed) &&
          event.categoriesAllowed.length > 0 && (
            <CategoryList
              categories={event.categoriesAllowed}
              onlyMacro
              withLabel
              macroIconClass="w-5 h-5"
            />
          )}
        {isAuthenticated && Number.isFinite(event.lowestPrice) && (
          <div className="flex flex-col items-end leading-tight">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
              {t('brand_detail.from')}
            </span>
            <span className="text-[15px] font-extrabold text-ink-strong tabular-nums">
              {fmtEUR(event.lowestPrice)}
            </span>
          </div>
        )}
      </div>
      <ChevronRightIcon className="w-4 h-4 text-gray-300 shrink-0" />
    </div>
  );
}

// --- BrandPastEventRow -----------------------------------------------------
// One row in the text-only past-events list.
export function BrandPastEventRow({ event }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const range = getEventDateRange(event.date);
  const monthYear = range
    ? `${moment(range.startDate).locale(i18n.language).format('MMM YYYY').toUpperCase()}`
    : '';
  const locationLine = [event.city, event.country].filter(Boolean).join(', ');

  const go = () => {
    if (event.slug) navigate(`/event/${event.slug}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="flex items-center gap-4 px-5 py-3.5 hover:bg-paper transition cursor-pointer group"
    >
      <div className="w-[78px] shrink-0 text-[10.5px] font-bold uppercase tracking-[0.12em] text-gray-400 tabular-nums">
        {monthYear}
      </div>
      <div className="w-1.5 h-1.5 rounded-full bg-line group-hover:bg-brand transition shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-extrabold text-ink-strong truncate group-hover:underline">
          {event.name}
        </div>
        {locationLine && (
          <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-gray-400">
            <MapPinIcon className="w-3 h-3 shrink-0" />
            <span className="truncate">{locationLine}</span>
          </div>
        )}
      </div>
      <ChevronRightIcon className="w-3 h-3 text-gray-300 group-hover:text-ink-strong shrink-0" />
    </div>
  );
}

// --- Website pill ----------------------------------------------------------
export function WebsitePill({ url }) {
  if (!url) return null;
  return (
    <a
      href={normalizeUrl(url)}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-paper hover:bg-line transition"
    >
      <GlobeAltIcon className="w-3.5 h-3.5 text-gray-600 shrink-0" />
      <span className="text-[12.5px] font-bold text-ink-strong truncate flex-1">
        {url}
      </span>
      <ArrowRightIcon className="w-3 h-3 text-gray-500 shrink-0" />
    </a>
  );
}

export { fmtRange, fmtMonthShort, daysCount, fmtEUR };
